import React, { useEffect, useState } from 'react';
import { documentsApi, policiesApi, providersApi, auditApi } from '../services/api';
import { DocumentDto, ProviderDto, PolicyDto, AuditLogDto } from '@shared/contracts';
import {
  LayoutDashboard,
  FileText,
  ShieldCheck,
  Cpu,
  Activity,
  BarChart3,
  Lock,
  Loader2,
  CheckCircle2,
  Clock,
  ArrowRight,
  Shield,
  AlertCircle,
} from 'lucide-react';
import { Link } from 'react-router-dom';

export const OverviewPage: React.FC = () => {
  const [documents, setDocuments] = useState<DocumentDto[]>([]);
  const [providers, setProviders] = useState<ProviderDto[]>([]);
  const [policies, setPolicies] = useState<PolicyDto[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLogDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchOverviewData = async () => {
    setIsLoading(true);
    setError('');
    try {
      const [docsData, provsData, polsData, logsData] = await Promise.all([
        documentsApi.list().catch(() => []),
        providersApi.list().catch(() => []),
        policiesApi.list().catch(() => []),
        auditApi.getLogs().catch(() => []),
      ]);
      setDocuments(docsData || []);
      setProviders(provsData || []);
      setPolicies(polsData || []);
      setAuditLogs(logsData || []);
    } catch (err) {
      console.error('Failed to load overview analytics API data', err);
      setError('Unable to load some overview metrics from backend services.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOverviewData();
  }, []);

  // Compute real metrics strictly from API response data
  const totalDocuments = documents.length;
  const totalPiiDetected = documents.reduce((acc, doc) => acc + (doc.entityCount || 0), 0);
  const activeProviders = providers.filter((p) => p.isActive);
  const completedDocs = documents.filter((doc) => doc.status === 'COMPLETED' || doc.status === 'PROCESSED');
  const redactionSuccessRate = totalDocuments > 0
    ? Math.round((completedDocs.length / totalDocuments) * 100)
    : 100;

  // Real Category / Regulation breakdown from API
  const categoryCounts: Record<string, number> = {};
  documents.forEach((doc) => {
    const cat = doc.privacyRegulation || doc.documentCategory || 'Standard Redaction';
    categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
  });

  const categoryBreakdown = Object.entries(categoryCounts).map(([name, count]) => ({
    name,
    count,
    percentage: totalDocuments > 0 ? Math.round((count / totalDocuments) * 100) : 0,
  })).sort((a, b) => b.count - a.count);

  const colors = ['bg-sky-500', 'bg-emerald-500', 'bg-purple-500', 'bg-amber-500', 'bg-indigo-500'];

  return (
    <div className="space-y-6 max-w-6xl mx-auto font-sans">
      {/* Header Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        <div>
          <h1 className="text-xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <LayoutDashboard className="w-5 h-5 text-sky-600" />
            <span>Platform Overview & Live Analytics</span>
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Real-time API metrics, active LLM engines, PII detection statistics, and audit activity.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchOverviewData}
            disabled={isLoading}
            className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-semibold transition-all flex items-center gap-1.5"
          >
            {isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin text-sky-600" /> : <Activity className="w-3.5 h-3.5 text-sky-600" />}
            <span>Refresh API Data</span>
          </button>
          <Link
            to="/dashboard"
            className="px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-xl text-xs font-semibold shadow-sm transition-all flex items-center gap-1.5"
          >
            <FileText className="w-4 h-4" />
            <span>Workspace</span>
          </Link>
        </div>
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Primary Metrics Row (Live API Data) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Documents */}
        <div className="p-5 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500 uppercase">Documents Ingested</span>
            <div className="p-2 rounded-lg bg-sky-50 text-sky-600">
              <FileText className="w-4 h-4" />
            </div>
          </div>
          {isLoading ? (
            <div className="h-8 flex items-center"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>
          ) : (
            <div className="text-2xl font-bold text-slate-900">{totalDocuments}</div>
          )}
          <div className="text-[11px] text-slate-500 font-medium">
            {completedDocs.length} successfully processed
          </div>
        </div>

        {/* PII Redacted */}
        <div className="p-5 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500 uppercase">PII Entities Redacted</span>
            <div className="p-2 rounded-lg bg-purple-50 text-purple-600">
              <ShieldCheck className="w-4 h-4" />
            </div>
          </div>
          {isLoading ? (
            <div className="h-8 flex items-center"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>
          ) : (
            <div className="text-2xl font-bold text-slate-900">{totalPiiDetected}</div>
          )}
          <div className="flex items-center gap-1 text-[11px] text-emerald-600 font-medium">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
            <span>{redactionSuccessRate}% Processing Success Rate</span>
          </div>
        </div>

        {/* Active AI Engines */}
        <div className="p-5 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500 uppercase">Active AI Engines</span>
            <div className="p-2 rounded-lg bg-amber-50 text-amber-600">
              <Cpu className="w-4 h-4" />
            </div>
          </div>
          {isLoading ? (
            <div className="h-8 flex items-center"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>
          ) : (
            <div className="text-2xl font-bold text-slate-900">{activeProviders.length} / {providers.length}</div>
          )}
          <div className="text-[11px] text-slate-500 font-medium truncate">
            {activeProviders.length > 0 ? activeProviders.map((p) => p.name).join(', ') : 'No Active AI Provider'}
          </div>
        </div>

        {/* Active Policies */}
        <div className="p-5 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500 uppercase">Redaction Policies</span>
            <div className="p-2 rounded-lg bg-emerald-50 text-emerald-600">
              <Shield className="w-4 h-4" />
            </div>
          </div>
          {isLoading ? (
            <div className="h-8 flex items-center"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>
          ) : (
            <div className="text-2xl font-bold text-slate-900">{policies.length} Policies</div>
          )}
          <div className="text-[11px] text-slate-500 font-medium">
            {policies.find((p) => p.isDefault)?.name ? `Default: ${policies.find((p) => p.isDefault)?.name}` : 'Zero Data Leakage Guaranteed'}
          </div>
        </div>
      </div>

      {/* Main Grid: Breakdown & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: API Dynamic Breakdown & Active Engines List */}
        <div className="lg:col-span-2 space-y-6">
          {/* Document Breakdown */}
          <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-sky-600" />
                <span>Document Framework & Category Distribution</span>
              </h2>
              <span className="text-xs text-slate-500 font-mono">Live API</span>
            </div>

            {isLoading ? (
              <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-sky-600" /></div>
            ) : categoryBreakdown.length > 0 ? (
              <div className="space-y-3">
                {categoryBreakdown.map((item, idx) => (
                  <div key={idx} className="space-y-1.5">
                    <div className="flex justify-between text-xs font-medium text-slate-700">
                      <span>{item.name}</span>
                      <span className="font-mono text-slate-500">{item.count} docs ({item.percentage}%)</span>
                    </div>
                    <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className={`h-full ${colors[idx % colors.length]} rounded-full transition-all duration-500`}
                        style={{ width: `${Math.max(item.percentage, 4)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center text-xs text-slate-400">
                No documents uploaded yet to compute framework distribution.
              </div>
            )}
          </div>

          {/* Connected AI Providers Telemetry */}
          <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <Cpu className="w-4 h-4 text-sky-600" />
                <span>AI Engine Provider Pool</span>
              </h2>
              <Link to="/providers" className="text-xs font-semibold text-sky-600 hover:underline">
                Manage Providers
              </Link>
            </div>

            {isLoading ? (
              <div className="py-6 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-sky-600" /></div>
            ) : providers.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {providers.map((provider) => (
                  <div key={provider.id} className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/80 flex items-center justify-between">
                    <div className="space-y-0.5">
                      <div className="text-xs font-bold text-slate-900">{provider.name}</div>
                      <div className="text-[11px] text-slate-500 font-mono">{provider.defaultModel}</div>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${
                        provider.isActive
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : 'bg-slate-200 text-slate-600'
                      }`}>
                        {provider.isActive ? 'Active' : 'Disabled'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-6 text-center text-xs text-slate-400">
                No AI Providers configured yet in backend.
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Real Audit Activity Stream */}
        <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <Clock className="w-4 h-4 text-sky-600" />
              <span>Audit Log Activity Stream</span>
            </h2>
            <Link to="/audit" className="text-xs font-semibold text-sky-600 hover:underline">
              View All
            </Link>
          </div>

          {isLoading ? (
            <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-sky-600" /></div>
          ) : auditLogs.length > 0 ? (
            <div className="space-y-3">
              {auditLogs.slice(0, 6).map((log, i) => (
                <div key={log.id || i} className="p-3 rounded-xl bg-slate-50 border border-slate-200/80 space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-semibold text-slate-800 font-mono truncate max-w-[170px]">
                      {log.action || 'ACTION_LOGGED'}
                    </span>
                    <span className="text-[10px] text-slate-400">
                      {log.timestamp ? new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Just now'}
                    </span>
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {log.userEmail || log.ipAddress || 'System Audit Trail'}
                  </div>
                </div>
              ))}
            </div>
          ) : documents.length > 0 ? (
            <div className="space-y-3">
              {documents.slice(0, 6).map((doc) => (
                <Link
                  key={doc.id}
                  to={`/documents/${doc.id}`}
                  className="p-3 rounded-xl bg-slate-50 hover:bg-sky-50/50 border border-slate-200/80 transition-colors block space-y-1"
                >
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-semibold text-slate-800 truncate max-w-[160px]">
                      {doc.originalName || doc.fileName}
                    </span>
                    <span className="text-[10px] font-mono bg-sky-100 text-sky-700 px-2 py-0.5 rounded">
                      {doc.entityCount || 0} PII
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-[11px] text-slate-500">
                    <span>{doc.countryName || 'Detected Country'}</span>
                    <span className="text-emerald-600 font-semibold">{doc.status || 'PROCESSED'}</span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400 text-xs">
              No audit logs recorded yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
